import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./BpsActionMenu.module.scss";
import { ThreeDotsIcon } from "../../../../assets/icons";

export type BpsActionMenuItem = {
	label: string;
	icon?: ReactNode;
	onClick: () => void;
	/** Renders the item in the destructive color. */
	danger?: boolean;
};

interface BpsActionMenuProps {
	items: BpsActionMenuItem[];
}

const MENU_GAP = 4;

/**
 * Row action menu for BPS admin tables. The dropdown renders in a portal because
 * the table body scrolls — an absolutely positioned menu would be clipped on the
 * bottom rows.
 */
export function BpsActionMenu({ items }: Readonly<BpsActionMenuProps>) {
	const [isOpen, setIsOpen] = useState(false);
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current || !menuRef.current) return;

		const trigger = triggerRef.current.getBoundingClientRect();
		const menu = menuRef.current.getBoundingClientRect();

		// Flip above / to the left when the menu would leave the viewport.
		const top =
			trigger.bottom + MENU_GAP + menu.height > window.innerHeight
				? trigger.top - menu.height - MENU_GAP
				: trigger.bottom + MENU_GAP;

		setPosition({ top, left: Math.max(MENU_GAP, trigger.right - menu.width) });
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const close = () => setIsOpen(false);
		const onPointerDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
			close();
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};

		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		// The menu is viewport-positioned, so any scroll would detach it from its row.
		window.addEventListener("scroll", close, true);
		window.addEventListener("resize", close);

		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("scroll", close, true);
			window.removeEventListener("resize", close);
		};
	}, [isOpen]);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				title="Actions"
				className={styles.trigger}
				onClick={() => setIsOpen(open => !open)}
			>
				<ThreeDotsIcon />
			</button>

			{isOpen &&
				createPortal(
					<div ref={menuRef} className={styles.menu} style={position}>
						{items.map(item => (
							<button
								key={item.label}
								type="button"
								className={`${styles.item} ${item.danger ? styles.danger : ""}`}
								onClick={() => {
									setIsOpen(false);
									item.onClick();
								}}
							>
								{item.icon}
								{item.label}
							</button>
						))}
					</div>,
					document.body,
				)}
		</>
	);
}
